import React, { useState, useImperativeHandle, forwardRef, ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Fade,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "Components/Form";
import { Colors } from "Components/Form/Button";
import { useTranslation } from "react-i18next";
import { colors } from "@zapalacorp/tokens";
import { getMuiPopupContainer } from "Themes/dispatchTheme";

// Tipos para configuração do modal
interface ModalConfig {
  title?: string;
  message?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  cancelColor?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  cancelCustomColor?: typeof Colors[keyof typeof Colors];
  confirmCustomColor?: typeof Colors[keyof typeof Colors];
}

// Tipos para o hook
interface UseConfirmationModalReturn {
  open: boolean;
  config: ModalConfig;
  openModal: (modalConfig: ModalConfig) => Promise<boolean>;
  closeModal: (result: boolean) => void;
}

// Tipos para a ref do modal
export interface AppConfirmationModalRef {
  open: (config: ModalConfig) => Promise<boolean>;
}

// Props do componente modal
interface AppConfirmationModalProps {
  maxWidthBreakpoint?: "xs" | "sm" | "md" | "lg" | "xl";
  maxWidth?: string;
}

// Hook personalizado para gerenciar o modal
const useAppConfirmationModal = (): UseConfirmationModalReturn => {
  const [open, setOpen] = useState<boolean>(false);
  const [config, setConfig] = useState<ModalConfig>({});
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);
  const { t: translate } = useTranslation();

  const openModal = (modalConfig: ModalConfig): Promise<boolean> => {
    setConfig({
      confirmText: translate("yes"),
      cancelText: translate("no"),
      confirmCustomColor: Colors.SUCCESS,
      cancelCustomColor: Colors.ERROR,
      ...modalConfig,
    });
    setOpen(true);

    return new Promise<boolean>((resolve) => {
      setResolvePromise(() => resolve);
    });
  };

  const closeModal = (result: boolean): void => {
    setOpen(false);
    if (resolvePromise) {
      resolvePromise(result);
      setResolvePromise(null);
    }
  };

  return {
    open,
    config,
    openModal,
    closeModal,
  };
};

// Componente do Modal
export const AppConfirmationModal = forwardRef<AppConfirmationModalRef, AppConfirmationModalProps>(
  ({ maxWidthBreakpoint = "sm", maxWidth }, ref) => {
    const { open, config, openModal, closeModal } = useAppConfirmationModal();

    const { t: translate } = useTranslation();

    useImperativeHandle(ref, () => ({
      open: openModal,
    }));

    const handleConfirm = (): void => {
      closeModal(true);
    };

    const handleCancel = (): void => {
      closeModal(false);
    };

    const handleClose = (_event: {}, reason?: string): void => {
      // Não fechar se o usuário clicou fora (backdropClick) ou pressionou ESC
      // para forçar uma escolha explícita
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        return;
      }
      closeModal(false);
    };

    const handleCloseButton = (): void => {
      closeModal(false);
    };

    return (
      <Dialog
        container={getMuiPopupContainer}
        open={open}
        onClose={handleClose}
        maxWidth={maxWidthBreakpoint}
        fullWidth
        disableEscapeKeyDown // Desabilita ESC para forçar escolha
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
        TransitionComponent={Fade}
        TransitionProps={{
          timeout: 300,
        }}
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
        PaperProps={{
          sx: {
            maxWidth,
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          id="confirmation-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "24px",
            pt: "24px",
            pb: 0,
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 600, color: colors.gray600, fontSize: "22px", lineHeight: "28px" }}
          >
            {config.title || translate("confirmation")}
          </Typography>
          <IconButton
            aria-label="Close modal"
            onClick={handleCloseButton}
            sx={{
              color: colors.gray600,
              "&:hover": {
                backgroundColor: colors.gray100,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: "32px !important" }}>
          <Typography
            id="confirmation-dialog-description"
            variant="body1"
            sx={{
              textAlign: "center",
              lineHeight: 1.6,
              color: colors.gray600,
              fontSize: "16px",
              letterSpacing: "0.5px",
            }}
          >
            {config.message || translate("areYouSure")}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${colors.gray200}`,
            gap: "16px",
            pt: "16px",
            pb: "16px",
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button debug customColor={config.confirmCustomColor} onClick={handleConfirm} autoFocus>
            {config.confirmText}
          </Button>
          <Button
            debug
            customColor={config.cancelCustomColor}
            onClick={handleCancel}
            sx={{ marginLeft: "0px !important" }}
          >
            {config.cancelText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
